/* The branded PDP "Customers Also Purchased" host in the hero column. It renders no cards of its
 * own: sections/lunya-pdp-recommendations.liquid owns the settings and the ranking scan, and its
 * page render is only a marker carrying the section id. This element fetches that section once
 * near the viewport and again after native cart changes or a linked-colour product swap, so
 * main-product's variant, preload and quick-buy renders never pay for the scan.
 * Shopify Liquid owns ranking/exclusions. No cart writes or inferred product/collection data.
 */
(() => {
  if (!window.customElements || customElements.get('lunya-pdp-recommendations')) return;
  const cartEvents = ['on:cart:add', 'on:cart:change', 'on:cart:after-merge'];

  class LunyaPdpRecommendations extends HTMLElement {
    constructor() {
      super();
      this.generation = 0;
      this.onCartChange = this.invalidate.bind(this);
      this.onPageShow = event => { if (event.persisted) this.invalidate(); };
      this.onBeforeProductReplace = event => {
        const { toReplace, replaceWith } = event.detail || {};
        // A row that has loaded refreshes for the new product at once: it is on screen, or was.
        // One that never loaded leaves the replacement to wait for the viewport like any other.
        if (toReplace?.contains(this) && this.requested) {
          replaceWith?.querySelector('lunya-pdp-recommendations')?.setAttribute('data-refresh-on-connect', '');
        }
      };
      // Theme editor: the Lunya also purchased section re-renders on its own when its settings
      // change, is re-added, or is removed; the copy in the product column follows it.
      this.onSectionLoad = event => {
        if (!this.isSourceEvent(event)) return;
        if (this.requested) this.invalidate();
        else this.watchViewport();
      };
      this.onSectionUnload = event => {
        if (!this.isSourceEvent(event)) return;
        this.generation++;
        this.cancelRequest();
        this.viewportObserver?.disconnect();
        this.viewportObserver = null;
        this.releaseCarousels();
        this.replaceChildren();
        this.removeAttribute('aria-busy');
        this.hidden = true;
        this.requested = false;
      };
    }

    connectedCallback() {
      cartEvents.forEach(name => document.addEventListener(name, this.onCartChange));
      window.addEventListener('pageshow', this.onPageShow);
      document.addEventListener('on:variant:before-replace-element', this.onBeforeProductReplace);
      document.addEventListener('shopify:section:load', this.onSectionLoad);
      document.addEventListener('shopify:section:unload', this.onSectionUnload);
      this.updateVisibility();
      // A reattached native carousel needs its listeners restored.
      if (this.needsNativeReinit) {
        this.querySelectorAll('carousel-slider').forEach(slider => slider.refresh());
        this.querySelectorAll('product-block').forEach(card => { if (!card.images) card.init(); });
        this.needsNativeReinit = false;
      } else {
        this.querySelectorAll('carousel-slider[loaded]').forEach(slider => slider.refresh());
      }
      if (this.hasAttribute('data-refresh-on-connect')) {
        this.removeAttribute('data-refresh-on-connect');
        this.invalidate();
      } else if (!this.requested) {
        this.watchViewport();
      }
    }

    disconnectedCallback() {
      cartEvents.forEach(name => document.removeEventListener(name, this.onCartChange));
      window.removeEventListener('pageshow', this.onPageShow);
      document.removeEventListener('on:variant:before-replace-element', this.onBeforeProductReplace);
      document.removeEventListener('shopify:section:load', this.onSectionLoad);
      document.removeEventListener('shopify:section:unload', this.onSectionUnload);
      // A first load cut off before any cards landed was never seen: let the reconnect ask again.
      if (this.hasAttribute('aria-busy') && !this.querySelector('product-block')) this.requested = false;
      this.viewportObserver?.disconnect();
      this.viewportObserver = null;
      this.generation++;
      this.cancelRequest();
      this.releaseCarousels();
      this.needsNativeReinit = true;
      this.removeAttribute('aria-busy');
      this.focusTarget = null;
    }

    cancelRequest() {
      clearTimeout(this.debounceTimer);
      clearTimeout(this.requestTimer);
      this.controller?.abort();
    }

    // The Lunya also purchased section's id, from the marker its page render leaves in the document.
    sourceId() {
      return document.querySelector('[data-lunya-recommendations-source]')?.dataset.sectionId || null;
    }

    // The host is `hidden` and has no box to observe; its block wrapper is a zero-height box at the
    // row's position, which IntersectionObserver still reports. Not the .shopify-section: that is
    // the whole of main-product, on screen from the start.
    // An editor section event about the Lunya also purchased section: by id, or by its marker.
    isSourceEvent(event) {
      const id = event.detail?.sectionId;
      const target = event.target;
      return (!!id && id === this.sourceId())
        || !!target?.matches?.('[data-lunya-recommendations-source]')
        || !!target?.querySelector?.('[data-lunya-recommendations-source]');
    }

    watchViewport() {
      this.viewportObserver?.disconnect();
      this.viewportObserver = null;
      if (!this.sourceId()) return;
      const target = this.closest('.lunya-also-purchased-block') || this.parentElement || this;
      const load = () => { if (!this.requested && this.isConnected) this.invalidate(); };
      // initLazyScript only looks ahead below the viewport. A shopper who arrives already past the
      // row (restored scroll, an anchor jump to reviews) would otherwise not load it until it came
      // back into view; load it now, ready for when they scroll up.
      if (target.getBoundingClientRect().bottom < 0) {
        load();
        return;
      }
      if (typeof window.initLazyScript === 'function') {
        this.viewportObserver = window.initLazyScript(target, load, 1000) || null;
      } else {
        load();
      }
    }

    releaseCarousels() {
      // Symmetry's carousel has no disconnectedCallback; dispose its window
      // resize listener before replacing/removing this dynamic subtree.
      this.querySelectorAll('carousel-slider[loaded]').forEach(slider => slider.removeListeners());
      this.querySelectorAll('carousel-slider, product-block').forEach(element => element.lazyInitObserver?.disconnect());
    }

    invalidate() {
      if (!this.isConnected) return;
      if (!this.sourceId()) { this.updateVisibility(); return; }
      this.requested = true;
      this.viewportObserver?.disconnect();
      this.viewportObserver = null;
      const generation = ++this.generation;
      this.cancelRequest();
      const active = document.activeElement;
      if (active !== this && this.contains(active)) {
        this.focusTarget = { href: active.getAttribute('href'), name: active.getAttribute('name') };
        this.focus({ preventScroll: true });
      }
      // Never leave a just-added product clickable while a refresh is pending
      // or failed. Keep the host so empty results can repopulate after removal.
      this.releaseCarousels();
      this.replaceChildren();
      // Stay out of landmark navigation while empty. Only a host holding focus stays shown, so
      // focus has somewhere to rest until the cards return.
      this.hidden = !this.focusTarget;
      this.setAttribute('aria-busy', 'true');
      this.debounceTimer = setTimeout(() => this.refresh(generation), 80);
    }

    async refresh(generation) {
      const controller = new AbortController();
      this.controller = controller;
      this.requestTimer = setTimeout(() => controller.abort(), 8000);
      try {
        const url = new URL(window.location.href);
        const productUrl = new URL(this.dataset.productUrl, url.origin);
        if (!this.dataset.productUrl || productUrl.origin !== url.origin) throw new Error('Invalid product URL');
        const localeRoot = window.Shopify?.routes?.root || '/';
        if (localeRoot !== '/' && productUrl.pathname.startsWith('/products/')) {
          productUrl.pathname = localeRoot.replace(/\/$/, '') + productUrl.pathname;
        }
        if (url.pathname !== productUrl.pathname) {
          // A linked Color can change products while size is unselected, before
          // native history changes. Old-product variant selectors must not leak.
          url.searchParams.delete('variant');
          url.searchParams.delete('option_values');
        }
        url.pathname = productUrl.pathname;
        url.hash = '';
        url.searchParams.delete('page');
        url.searchParams.delete('section_id');
        const sourceId = this.sourceId();
        if (!sourceId) throw new Error('Recommendation section missing');
        url.searchParams.set('sections', sourceId);
        const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', signal: controller.signal });
        if (!response.ok) throw new Error('Recommendation section unavailable');
        const sections = await response.json();
        const html = sections[sourceId];
        if (typeof html !== 'string') throw new Error('Missing recommendation section');
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const incoming = [...doc.querySelectorAll('[data-lunya-recommendations-source]')].find(element =>
          element.dataset.currentProductId === this.dataset.currentProductId);
        if (!incoming) throw new Error('Recommendation identity changed');
        if (generation !== this.generation || !this.isConnected) return;
        // The editor's preview note belongs to the section's own slot, never the product column.
        incoming.querySelectorAll('[data-lunya-recommendations-note]').forEach(element => element.remove());
        incoming.querySelectorAll('[data-cc-animate]').forEach(element => element.removeAttribute('data-cc-animate'));
        this.innerHTML = incoming.innerHTML;
        // Name the region by the heading the section rendered: the block's own heading setting is
        // now an adapter-off fallback, so the label it seeded can be stale.
        const heading = this.querySelector('.lunya-also-purchased__heading');
        if (heading) {
          const name = heading.textContent.trim();
          if (name) this.setAttribute('aria-label', name);
          else this.removeAttribute('aria-label'); // a cleared heading must not keep the old name
        }
        if (document.activeElement === this && this.focusTarget) {
          const target = [...this.querySelectorAll('a[href], button[name]')].find(element =>
            this.focusTarget.href ? element.getAttribute('href') === this.focusTarget.href :
              this.focusTarget.name && element.getAttribute('name') === this.focusTarget.name);
          target?.focus({ preventScroll: true });
        }
      } catch (error) {
        // Best effort: keep the invalidated row empty, never restore stale cards.
        // Subsequent cart events retry independently; native commerce is untouched.
      } finally {
        // A superseded response must not clear the new request's timer/state.
        if (generation === this.generation && this.isConnected) {
          clearTimeout(this.requestTimer);
          this.removeAttribute('aria-busy');
          this.focusTarget = null;
          this.updateVisibility();
        }
      }
    }

    updateVisibility() {
      const empty = !this.querySelector('product-block');
      if (empty && document.activeElement === this) {
        const form = this.closest('product-form');
        const target = [form?.querySelector('[name="add"]'), form?.querySelector('.product-title'), form]
          .find(element => element && !element.disabled && element.getClientRects().length && getComputedStyle(element).visibility !== 'hidden');
        if (target) {
          const temporaryTabIndex = !target.hasAttribute('tabindex') && target.tabIndex < 0;
          if (temporaryTabIndex) {
            target.setAttribute('tabindex', '-1');
            target.addEventListener('blur', () => target.removeAttribute('tabindex'), { once: true });
          }
          target.focus({ preventScroll: true });
        }
      }
      this.hidden = empty;
    }
  }
  customElements.define('lunya-pdp-recommendations', LunyaPdpRecommendations);
})();
